import { useEffect, useState } from "react";
import { ScrollView, useWindowDimensions } from "react-native";
import {
  Dialog,
  DialogActions,
  DialogHeader,
  PrimaryButton,
} from "@louise/moon-ui";
import { useTranslations } from "@src/packages/i18n";
import {
  pressLabel,
  usePressCapture,
} from "@src/packages/analytics/usePressCapture";
import {
  FrozenSpecimenSelector,
  type FrozenSpecimenSelection,
  type PickerSpecimen,
} from "@src/packages/care/cryovault/FrozenSpecimenSelector";

export type { FrozenSpecimenSelection } from "@src/packages/care/cryovault/FrozenSpecimenSelector";

type Props = {
  visible: boolean;
  type: "oocyte" | "embryo" | null;
  initialSpecimenIds?: string[];
  onBack: () => void;
  onConfirm: (selection: FrozenSpecimenSelection) => void;
  isLoading?: boolean;
  /**
   * When present, restrict the picker to these specimen IDs. Used by the
   * CryoVault's virtual-cohort tile to open a picker scoped to one retrieval
   * batch instead of the whole vault.
   */
  scopedSpecimenIds?: readonly string[];
  /**
   * When present, use this list as the source instead of `useFrozenSpecimens`.
   * A real oocyte cohort's members are not addressable through the specimens
   * query — the CryoVault reconstructs `PickerSpecimen` shapes from the
   * cohort's members and passes them here.
   */
  overrideSpecimens?: readonly PickerSpecimen[];
};

export function SelectFrozenSpecimensModal({
  visible,
  type,
  initialSpecimenIds,
  onBack,
  onConfirm,
  isLoading = false,
  scopedSpecimenIds,
  overrideSpecimens,
}: Props) {
  const t = useTranslations();
  const capturePress = usePressCapture("SelectFrozenSpecimensModal");
  const { height: windowHeight } = useWindowDimensions();
  const dialogMaxHeight = Math.min(windowHeight - 32, 720);
  const [selection, setSelection] = useState<FrozenSpecimenSelection>({
    specimenIds: initialSpecimenIds ?? [],
    sourceCycleId: null,
  });
  const [canConfirmSelection, setCanConfirmSelection] = useState(false);
  const showConfirmation =
    canConfirmSelection && selection.specimenIds.length > 0;
  // Reserve room for the centered header and, only after a valid pick, its action.
  const scrollMaxHeight = Math.max(
    200,
    dialogMaxHeight - (showConfirmation ? 200 : 140),
  );

  useEffect(() => {
    if (visible) {
      setSelection({
        specimenIds: initialSpecimenIds ?? [],
        sourceCycleId: null,
      });
    }
  }, [visible, initialSpecimenIds]);

  if (!type) return null;

  const isEmbryo = type === "embryo";
  const title = isEmbryo
    ? t("care.select_specimens.choose_embryos_for_this_cycle")
    : t("care.select_specimens.choose_eggs_for_this_cycle");
  const subtitle = isEmbryo
    ? t("care.select_specimens.pick_embryos_for_this_transfer")
    : t("care.select_specimens.pick_frozen_eggs_to_thaw_and_fertilize");
  const confirmDisabled =
    isLoading || !canConfirmSelection || selection.specimenIds.length === 0;
  const confirmLabel = isLoading
    ? t("care.select_specimens.confirming_selection")
    : t("care.select_specimens.confirm_selection");

  return (
    <Dialog
      containerStyle={{ maxHeight: dialogMaxHeight }}
      size="wide"
      visible={visible}
      onClose={() => {
        capturePress("dismiss");
        onBack();
      }}
      layout="centered"
      closeButton="minimal"
      dismissOnBackdropTap={!isLoading}
      contentStyle={{
        paddingHorizontal: 24,
        paddingBottom: 24,
      }}
    >
      <DialogHeader title={title} subtitle={subtitle} align="center" />

      <ScrollView
        showsVerticalScrollIndicator={false}
        keyboardShouldPersistTaps="handled"
        style={{ marginTop: 16, maxHeight: scrollMaxHeight }}
        contentContainerStyle={{ paddingBottom: 0 }}
      >
        <FrozenSpecimenSelector
          type={type}
          selectedIds={selection.specimenIds}
          onChange={setSelection}
          onCanConfirmChange={setCanConfirmSelection}
          scopedSpecimenIds={scopedSpecimenIds}
          overrideSpecimens={overrideSpecimens}
        />
      </ScrollView>

      {showConfirmation ? (
        <DialogActions style={{ marginTop: 12 }}>
          <PrimaryButton
            label={confirmLabel}
            onPress={() => {
              if (confirmDisabled) return;
              capturePress(
                pressLabel(t("care.select_specimens.confirm_selection")),
                "care.select_specimens.confirm",
              );
              onConfirm(selection);
            }}
            disabled={confirmDisabled}
            loading={isLoading}
            flex={1}
          />
        </DialogActions>
      ) : null}
    </Dialog>
  );
}
